import { Button } from '@/src/common/components/Button'
import { type ComponentProps, type FunctionComponent, useEffect } from 'react'
import { ScrollView, View, Text, StyleSheet } from 'react-native'
import { Like } from '@/src/assets/icons/Like'
import { OptionItemSelector } from '@/src/app/main/(tabs)/card/card-management/payment/optionItemSelector'
import { PaymentCardInformation } from '@/src/app/main/(tabs)/card/card-management/_common/paymentCardInformation'
import { usePaymentScreen } from '@/src/experiences/card-payments/hooks/usePaymentScreen'
import { mesaColorNames, typography } from '@/src/common/theme'
import { provideCardPlatform } from '@/src/experiences/card-platform/provideCardPlatform'
import { useLegacyAutoPayStore } from '@/src/experiences/card-payments/store/useLegacyAutoPayStore'
import { useRecurringPaymentStore } from '@/src/experiences/card-payments/store/useRecurringPaymentStore'
import { formatRecurringPaymentStrategy } from '@/src/experiences/card-payments/screens/AutoPayReview/PlatformAutoPayReview'
import { formatToTextMonth } from '@/src/common/utils/date'

export function useAutoPaySuccessScreenProps() {
  return usePaymentScreen('autoPay/paymentSuccessful', 'Payment Confirmation', false)
}

type AutoPaySuccessComponentType = FunctionComponent<{
  paymentStrategy?: ComponentProps<typeof OptionItemSelector>['title']
  day?: ComponentProps<typeof OptionItemSelector>['title']
  account?: ComponentProps<typeof OptionItemSelector>['title']
  clear?: () => void
}>

function LegacyAutoPaySuccess({
  AutoPaySuccessComponent,
}: {
  AutoPaySuccessComponent: AutoPaySuccessComponentType
}) {
  const { clear, paymentStrategy, day, account } = useLegacyAutoPayStore()
  return (
    <AutoPaySuccessComponent
      paymentStrategy={paymentStrategy?.label}
      day={day?.label}
      account={account?.last4}
      clear={clear}
    />
  )
}

function PlatformAutoPaySuccess({
  AutoPaySuccessComponent,
}: {
  AutoPaySuccessComponent: AutoPaySuccessComponentType
}) {
  const { paymentStrategy, day, account, clear } = useRecurringPaymentStore()
  return (
    <AutoPaySuccessComponent
      paymentStrategy={formatRecurringPaymentStrategy(paymentStrategy)}
      day={day ? formatToTextMonth(new Date(day), {}) : undefined}
      account={account?.last4}
      clear={clear}
    />
  )
}

const AutoPaySuccess = provideCardPlatform<AutoPaySuccessComponentType>(({ ToWrap, platform }) => {
  switch (platform) {
    case 'HIGHNOTE':
      return <LegacyAutoPaySuccess AutoPaySuccessComponent={ToWrap} />
    case 'LOANPRO':
      return <PlatformAutoPaySuccess AutoPaySuccessComponent={ToWrap} />
    default:
      platform satisfies undefined
      return <ToWrap />
  }
})(({ paymentStrategy, day, account, clear }) => {
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => clear, [])

  const { Modal, dismiss } = useAutoPaySuccessScreenProps()

  return Modal({
    children: (
      <ScrollView contentContainerStyle={{ paddingBottom: 50 }}>
        <View style={styles.centeredContainer}>
          <View style={styles.iconWrapper}>
            <Like />
          </View>
          <Text style={[typography['heading/heading4'], styles.successMessage]}>
            Auto-pay successfully scheduled
          </Text>
        </View>
        <PaymentCardInformation containerStyle={{ marginHorizontal: 16, position: 'relative' }}>
          <OptionItemSelector label="Amount" title={paymentStrategy} />
          <OptionItemSelector label="Date" title={day} />
          <OptionItemSelector label="Bank Account" title={account} withBorder={false} />
        </PaymentCardInformation>
        <Button
          testID="CardPayments.Button.AutoPayConfirmation"
          title="Done"
          onPress={() => void dismiss()}
        />
      </ScrollView>
    ),
  })
})

const styles = StyleSheet.create({
  centeredContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    margin: 16,
  },
  iconWrapper: {
    borderRadius: 50,
    width: 56,
    height: 56,
    backgroundColor: mesaColorNames['Mesa Primary Scale/400 - Brand #00DFC9'],
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  successMessage: {
    color: mesaColorNames['Mesa - Brand/White #FFFFFF'],
    marginVertical: 16,
  },
})

export const AutoPaySuccessScreen = {
  name: 'auto-pay-schedule',
  Screen: AutoPaySuccess,
} as const
